export interface TikTokPurchase {
  content_id: string;
  content_type: 'product' | 'product_group';
  content_name?: string;
  quantity: number;
  price: number;
  value: number;
  currency: string;
}

declare global {
  interface Window {
    ttq?: {
      track?: (eventName: string, payload: TikTokPurchase) => void;
    };
  }
}

let pendingPurchase: TikTokPurchase | null = null;
const STORAGE_PREFIX = 'watchi_tiktok_complete_payment:';

// Stripe is the source of truth for the product, paid amount and currency.
// Keys are the Price IDs used by the WF checkout flows.
const STRIPE_PURCHASES: Record<string, {
  productId: string;
  productName: string;
  amount: number;
  currency: string;
}> = {
  price_1SjIs3HsfUxB19MUDCUBkAk0: {
    productId: 'prod_TggEwzYTrWa6k5',
    productName: 'Watchi 1-Month Premium Subscription',
    amount: 6.99,
    currency: 'USD',
  },
  price_1ST8xyHsfUxB19MUtGoSjTC9: {
    productId: 'prod_TPyvp9t3VIZwhu',
    productName: 'Watchi 1-Month Premium Subscription',
    amount: 0.99,
    currency: 'USD',
  },
  price_1SdqckHsfUxB19MUBXUtVOFS: {
    productId: 'prod_Tb2iigw7rRRgVc',
    productName: 'Watchi 1-Month Premium Subscription',
    amount: 2.99,
    currency: 'GBP',
  },
  price_1UGOBjHsfUxB19MULbD9gP0F: {
    productId: 'prod_Tb2iigw7rRRgVc',
    productName: 'Watchi 1-Month Premium Subscription',
    amount: 0.99,
    currency: 'GBP',
  },
  price_1T6EUxHsfUxB19MUzpyATf0g: {
    productId: 'prod_U4NFFncyHRIdba',
    productName: 'Watchi 1-Month Premium Subscription',
    amount: 2.99,
    currency: 'CAD',
  },
  price_1UGOHlHsfUxB19MUBr5vxlD4: {
    productId: 'prod_U4NFFncyHRIdba',
    productName: 'Watchi 1-Month Premium Subscription',
    amount: 0.99,
    currency: 'CAD',
  },
  price_1T6F6bHsfUxB19MUZbKwAARK: {
    productId: 'prod_U4NsYyhl7ux14G',
    productName: 'Watchi 1-Monats-Premium-Abonnement',
    amount: 2.99,
    currency: 'EUR',
  },
  price_1UGNQrHsfUxB19MU3ryoMFdF: {
    productId: 'prod_U4NsYyhl7ux14G',
    productName: 'Watchi 1-Monats-Premium-Abonnement',
    amount: 0.99,
    currency: 'EUR',
  },
  price_1T6FU8HsfUxB19MUtCfJKXl6: {
    productId: 'prod_U4OGmUapu4TwFw',
    productName: "Abonnement Premium Watchi d'un mois",
    amount: 2.99,
    currency: 'EUR',
  },
  price_1UGO83HsfUxB19MUIgf7h3Fu: {
    productId: 'prod_U4OGmUapu4TwFw',
    productName: "Abonnement Premium Watchi d'un mois",
    amount: 0.99,
    currency: 'EUR',
  },
};

export function stageTikTokCompletePayment(purchase: TikTokPurchase): void {
  pendingPurchase = purchase;
}

export function trackTikTokCompletePayment(
  fallbackPurchase: TikTokPurchase,
  eventKey: string,
): boolean {
  try {
    const purchase = pendingPurchase || fallbackPurchase;
    pendingPurchase = null;

    if (typeof window === 'undefined' || typeof window.ttq?.track !== 'function') {
      return false;
    }

    const stripePurchase = STRIPE_PURCHASES[purchase.content_id];

    const payload: TikTokPurchase = {
      content_id: stripePurchase?.productId || String(purchase.content_id || ''),
      content_type: purchase.content_type === 'product_group' ? 'product_group' : 'product',
      content_name: stripePurchase?.productName || (purchase.content_name ? String(purchase.content_name) : undefined),
      quantity: 1,
      price: stripePurchase?.amount ?? Number(purchase.price),
      value: stripePurchase?.amount ?? Number(purchase.value),
      currency: stripePurchase?.currency || String(purchase.currency || '').toUpperCase(),
    };

    if (
      !payload.content_id ||
      !Number.isFinite(payload.price) ||
      !Number.isFinite(payload.value) ||
      payload.price <= 0 ||
      payload.value <= 0 ||
      !/^[A-Z]{3}$/.test(payload.currency)
    ) {
      console.warn('TikTok CompletePayment was not sent: invalid purchase data');
      return false;
    }

    const storageKey = `${STORAGE_PREFIX}${eventKey}`;
    if (window.sessionStorage.getItem(storageKey)) return false;

    window.ttq.track('CompletePayment', payload);
    window.sessionStorage.setItem(storageKey, '1');
    return true;
  } catch (error) {
    // Analytics must never block checkout or the success screen.
    console.error('TikTok Pixel CompletePayment error:', error);
    return false;
  }
}
